import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiCheck, FiTrash2, FiUser, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import './HR.css';

const CATEGORIES = ['Admin','IT Setup','HR','Finance','Culture','Work','Other'];

export default function Onboarding() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [members, setMembers]   = useState([]);
  const [selUser, setSelUser]   = useState(null);
  const [tasks, setTasks]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [addForm, setAddForm]   = useState({ title:'', category:'General', dueDate:'' });
  const [showAdd, setShowAdd]   = useState(false);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    if (isAdmin) api.get('/users').then(r => setMembers((r.data.users||[]).filter(u => u.role !== 'client')));
    else { setSelUser(user); loadTasks(user.id); }
  }, []);

  const loadTasks = async (uid) => {
    setLoading(true);
    try {
      const r = await api.get(`/onboarding/${uid}`);
      setTasks(r.data.tasks || []);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  const selectUser = (u) => { setSelUser(u); loadTasks(u._id || u.id); };

  const initTasks = async () => {
    try {
      await api.post(`/onboarding/${selUser._id || selUser.id}/init`);
      toast.success('Default onboarding tasks created!');
      loadTasks(selUser._id || selUser.id);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const toggle = async (task) => {
    try {
      const r = await api.patch(`/onboarding/${task.id}/toggle`);
      setTasks(prev => prev.map(t => t.id === task.id ? r.data.task : t));
    } catch { toast.error('Failed'); }
  };

  const addTask = async () => {
    if (!addForm.title.trim()) return toast.error('Title required');
    try {
      const r = await api.post(`/onboarding/${selUser._id || selUser.id}`, addForm);
      setTasks(prev => [...prev, r.data.task]);
      setAddForm({ title:'', category:'General', dueDate:'' });
      setShowAdd(false);
      toast.success('Task added');
    } catch { toast.error('Failed'); }
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/onboarding/${id}`);
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch { toast.error('Failed'); }
  };

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const cats = tasks.filter(t => t.category === cat || (!CATEGORIES.includes(t.category) && cat === 'Other'));
    if (cats.length) acc[cat] = cats;
    return acc;
  }, {});

  const total = tasks.length;
  const done  = tasks.filter(t => t.completed).length;
  const pct   = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="hr-page">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0 }}>Employee Onboarding</h1>
          <p style={{ margin:'4px 0 0', color:'#94a3b8', fontSize:13 }}>Track new hire checklist progress</p>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isAdmin ? '260px 1fr' : '1fr', gap:20 }}>
        {/* Employee List (admin) */}
        {isAdmin && (
          <div className="card" style={{ padding:16, height:'fit-content' }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#475569', marginBottom:12 }}>Select Employee</div>
            {members.map(m => (
              <div key={m._id||m.id} onClick={() => selectUser(m)}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10, cursor:'pointer', marginBottom:4,
                  background: selUser?._id===m._id||selUser?.id===m.id ? 'var(--pm-gradient)' : '#f8fafc',
                  color: selUser?._id===m._id||selUser?.id===m.id ? '#fff' : '#0f172a' }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background: selUser?._id===m._id||selUser?.id===m.id ? 'rgba(255,255,255,0.3)' : '#e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13 }}>
                  {m.name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{m.name}</div>
                  <div style={{ fontSize:11, opacity:.7 }}>{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Task Panel */}
        <div>
          {!selUser ? (
            <div className="card" style={{ padding:60, textAlign:'center', color:'#94a3b8' }}>
              <FiUser size={40} style={{ marginBottom:12, display:'block', margin:'0 auto 12px' }}/>
              Select an employee to view their onboarding checklist
            </div>
          ) : (
            <div className="card">
              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                  <h3 style={{ margin:0, fontSize:17, fontWeight:700 }}>{selUser.name}'s Onboarding</h3>
                  {total > 0 && <p style={{ margin:'4px 0 0', color:'#64748b', fontSize:13 }}>{done}/{total} tasks completed ({pct}%)</p>}
                </div>
                {isAdmin && (
                  <div style={{ display:'flex', gap:8 }}>
                    {total === 0 && <button onClick={initTasks} style={{ padding:'8px 16px', borderRadius:10, border:'none', background:'#10b981', color:'#fff', fontWeight:600, cursor:'pointer', fontSize:13 }}>⚡ Initialize Default</button>}
                    <button onClick={() => setShowAdd(s=>!s)} style={{ padding:'8px 16px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', fontWeight:600, cursor:'pointer', fontSize:13, display:'flex', alignItems:'center', gap:6 }}><FiPlus/> Add Task</button>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              {total > 0 && (
                <div style={{ height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden', marginBottom:20 }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:'#10b981', borderRadius:4, transition:'width .5s' }}/>
                </div>
              )}

              {/* Add form */}
              {showAdd && (
                <div style={{ padding:16, background:'#f8fafc', borderRadius:12, marginBottom:16, display:'grid', gridTemplateColumns:'1fr auto auto auto', gap:10, alignItems:'end' }}>
                  <div>
                    <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>Task Title *</label>
                    <input style={{ width:'100%', padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                      value={addForm.title} onChange={e=>setAddForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Share employee handbook"/>
                  </div>
                  <div>
                    <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>Category</label>
                    <select style={{ padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}
                      value={addForm.category} onChange={e=>setAddForm(f=>({...f,category:e.target.value}))}>
                      {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize:11, fontWeight:700, color:'#64748b', display:'block', marginBottom:4 }}>Due Date</label>
                    <input type="date" style={{ padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}
                      value={addForm.dueDate} onChange={e=>setAddForm(f=>({...f,dueDate:e.target.value}))}/>
                  </div>
                  <button onClick={addTask} style={{ padding:'8px 16px', background:'#10b981', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>Add</button>
                </div>
              )}

              {loading ? <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Loading…</div>
              : total === 0 ? <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>No tasks yet. {isAdmin && 'Click "Initialize Default" to add standard onboarding steps.'}</div>
              : Object.entries(grouped).map(([cat, catTasks]) => (
                <div key={cat} style={{ marginBottom:8 }}>
                  <div onClick={() => setCollapsed(c=>({...c,[cat]:!c[cat]}))}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 4px', cursor:'pointer', userSelect:'none' }}>
                    {collapsed[cat] ? <FiChevronRight size={14}/> : <FiChevronDown size={14}/>}
                    <span style={{ fontWeight:700, fontSize:12, color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em' }}>{cat}</span>
                    <span style={{ fontSize:11, color:'#94a3b8' }}>{catTasks.filter(t=>t.completed).length}/{catTasks.length}</span>
                  </div>
                  {!collapsed[cat] && catTasks.map(task => (
                    <div key={task.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 12px', borderRadius:10, marginBottom:4, background: task.completed ? '#f0fdf4' : '#fafbfc', border:'1px solid', borderColor: task.completed ? '#bbf7d0' : '#f1f5f9' }}>
                      <div onClick={() => toggle(task)}
                        style={{ width:22, height:22, borderRadius:6, border:`2px solid ${task.completed ? '#10b981' : '#e2e8f0'}`, background: task.completed ? '#10b981' : '#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'all .2s' }}>
                        {task.completed && <FiCheck color="#fff" size={13}/>}
                      </div>
                      <div style={{ flex:1 }}>
                        <span style={{ fontSize:14, fontWeight:600, color: task.completed ? '#6b7280' : '#0f172a', textDecoration: task.completed ? 'line-through' : 'none' }}>{task.title}</span>
                        {task.dueDate && <span style={{ fontSize:11, color:'#94a3b8', marginLeft:10 }}>Due {new Date(task.dueDate).toLocaleDateString()}</span>}
                        {task.completed && task.completedBy && <span style={{ fontSize:11, color:'#10b981', marginLeft:8 }}>✓ {task.completedBy.name}</span>}
                      </div>
                      {isAdmin && <button onClick={() => deleteTask(task.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#f87171', padding:4 }}><FiTrash2 size={14}/></button>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
